import { useEffect } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';

import { logisticsApi } from 'api/logistics';
import { procurementApi } from 'api/procurement';
import { warehouseApi } from 'api/warehouse';

// ==================== Query Keys ====================

export const purchaseOrderQueryKeys = {
    all: ['purchaseOrders'],
    // Distinct keys for infinite vs paginated to avoid React Query cache conflicts
    searchInfinite: (filters) => ['purchaseOrders', 'v2', 'search', 'infinite', filters],
    searchPaginated: (filters) => ['purchaseOrders', 'v2', 'search', 'paginated', filters],
    // Legacy key for backwards compatibility
    search: (filters) => ['purchaseOrders', 'v2', 'search', filters],
    filters: {
        statuses: () => ['purchaseOrders', 'filters', 'statuses'],
        externalSkus: (search) => ['purchaseOrders', 'filters', 'externalSkus', search],
        vendors: () => ['purchaseOrders', 'filters', 'vendors'],
        facilities: () => ['purchaseOrders', 'filters', 'facilities'],
        buyers: () => ['purchaseOrders', 'filters', 'buyers'],
        shippers: () => ['purchaseOrders', 'filters', 'shippers'],
        crossDocks: () => ['purchaseOrders', 'filters', 'crossDocks'],
    },
    detail: (id) => ['purchaseOrders', 'detail', id],
};

// Legacy keys for backward compatibility
export const REMOTE_SEARCH_FILTER_KEY = 'externalSkuSearch';
export const PURCHASE_ORDERS_QUERY_KEY = 'purchase-orders';

// ==================== V2 Purchase Order Search (Cursor-based) ====================

/**
 * Hook for searching purchase orders with cursor-based infinite scroll.
 * This is the primary hook for the PO V2 search page.
 *
 * @param {Object} filters - Search filters (vendorIds, facilityIds, statuses, etc.)
 * @param {Object} options - React Query options
 * @returns {import('@tanstack/react-query').UseInfiniteQueryResult}
 */
export const usePurchaseOrdersV2Infinite = (filters = {}, options = {}) =>
    useInfiniteQuery({
        queryKey: purchaseOrderQueryKeys.searchInfinite(filters),
        queryFn: async ({ pageParam, signal }) => {
            const response = await procurementApi.searchPurchaseOrdersV2(
                {
                    ...filters,
                    cursor: pageParam?.cursor,
                    totalCount: pageParam?.totalCount,
                },
                { signal }, // Pass AbortSignal for automatic cancellation
            );
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch purchase orders');
            }
            return response.data;
        },
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => {
            if (!lastPage?.pagination?.hasMore) return undefined;
            return {
                cursor: lastPage.pagination.nextCursor,
                totalCount: lastPage.pagination.totalCount,
            };
        },
        staleTime: 30 * 1000,
        ...options,
    });

/**
 * Hook to prefetch the next page of purchase orders.
 * Call this when the user is approaching the end of the current data.
 *
 * @param {Object} filters - Current search filters
 * @param {Object} pagination - Current pagination state
 * @param {number} pagination.nextCursor - Cursor for the next page
 * @param {number} pagination.totalCount - Total count to pass through
 * @param {boolean} pagination.hasMore - Whether there are more pages
 */
export const usePrefetchNextPage = (filters, pagination) => {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!pagination?.hasMore || !pagination?.nextCursor) {
            return;
        }

        // Prefetch the next page
        queryClient.prefetchInfiniteQuery({
            queryKey: purchaseOrderQueryKeys.searchInfinite(filters),
            queryFn: async () => {
                const response = await procurementApi.searchPurchaseOrdersV2({
                    ...filters,
                    cursor: pagination.nextCursor,
                    totalCount: pagination.totalCount,
                });
                if (!response?.status) {
                    throw new Error(response?.msg ?? 'Failed to fetch purchase orders');
                }
                return response.data;
            },
            initialPageParam: undefined,
            staleTime: 30 * 1000,
        });
    }, [filters, pagination?.nextCursor, pagination?.hasMore, pagination?.totalCount, queryClient]);
};

/**
 * Hook for single-page purchase order search (non-infinite).
 * Useful when you only need the first page of results.
 *
 * @param {Object} params - Search parameters including filters
 * @param {Object} options - React Query options
 */
export const usePurchaseOrdersV2Search = (params = {}, options = {}) =>
    useQuery({
        queryKey: purchaseOrderQueryKeys.searchPaginated(params),
        queryFn: async ({ signal }) => {
            const response = await procurementApi.searchPurchaseOrdersV2(
                params,
                { signal }, // Pass AbortSignal for automatic cancellation
            );
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch purchase orders');
            }
            return response.data;
        },
        staleTime: 30 * 1000,
        ...options,
    });

// ==================== Filter Options Hooks ====================

/**
 * Hook for fetching PO status options.
 */
export const useStatusOptions = (options = {}) =>
    useQuery({
        queryKey: purchaseOrderQueryKeys.filters.statuses(),
        queryFn: async () => {
            const response = await procurementApi.getStatusOptions();
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch status options');
            }
            return response.data?.statuses ?? [];
        },
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        ...options,
    });

/**
 * Hook for searching external SKUs.
 * @param {string} search - Search term
 * @param {Object} options - React Query options
 */
export const useExternalSkuSearch = (search = '', options = {}) =>
    useQuery({
        queryKey: purchaseOrderQueryKeys.filters.externalSkus(search),
        queryFn: async () => {
            const response = await procurementApi.searchExternalSkus(search);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch external SKUs');
            }
            return response.data?.externalSkus ?? [];
        },
        staleTime: 60 * 1000,
        enabled: options.enabled !== false,
        ...options,
    });

/**
 * Hook for fetching vendor options.
 */
export const useVendorOptions = (options = {}) =>
    useQuery({
        queryKey: purchaseOrderQueryKeys.filters.vendors(),
        queryFn: async () => {
            const response = await procurementApi.getVendors(true);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch vendors');
            }
            const vendors = response.data?.rows ?? response.data ?? [];
            return vendors.map((v) => ({
                value: v.id,
                label: v.name,
            }));
        },
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        ...options,
    });

/**
 * Hook for fetching facility options.
 */
export const useFacilityOptions = (options = {}) =>
    useQuery({
        queryKey: purchaseOrderQueryKeys.filters.facilities(),
        queryFn: async () => {
            const response = await warehouseApi.getFacilities(true);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch facilities');
            }
            const facilities = response.data?.rows ?? response.data ?? [];
            return facilities.map((f) => ({
                value: f.id,
                label: f.name,
            }));
        },
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        ...options,
    });

/**
 * Hook for fetching buyer options.
 * API returns: { id, badgeNumber, firstName, lastName, email }
 */
export const useBuyerOptions = (options = {}) =>
    useQuery({
        queryKey: purchaseOrderQueryKeys.filters.buyers(),
        queryFn: async () => {
            const response = await procurementApi.getBuyers();
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch buyers');
            }
            const buyers = response.data?.rows ?? response.data ?? [];
            return buyers.map((b) => ({
                value: b.id,
                label: `${b.firstName} ${b.lastName}`.trim() || `Buyer ${b.id}`,
            }));
        },
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        ...options,
    });

/**
 * Hook for fetching shipper options.
 * API returns: { id, name, ... }
 */
export const useShipperOptions = (options = {}) =>
    useQuery({
        queryKey: purchaseOrderQueryKeys.filters.shippers(),
        queryFn: async () => {
            const response = await procurementApi.getShippers(true);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch shippers');
            }
            const shippers = response.data?.rows ?? response.data ?? [];
            return shippers.map((s) => ({
                value: s.id,
                label: s.name,
            }));
        },
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        ...options,
    });

/**
 * Hook for fetching cross dock options.
 * API returns: { id, name, ... }
 */
export const useCrossDockOptions = (options = {}) =>
    useQuery({
        queryKey: purchaseOrderQueryKeys.filters.crossDocks(),
        queryFn: async () => {
            const response = await logisticsApi.getInboundCrossDocks(true);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch cross docks');
            }
            const crossDocks = response.data?.rows ?? response.data ?? [];
            return crossDocks.map((cd) => ({
                value: cd.id,
                label: cd.name,
            }));
        },
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        ...options,
    });

// ==================== Legacy Hooks (for backward compatibility) ====================

/**
 * @deprecated Use usePurchaseOrdersV2Infinite or usePurchaseOrdersV2Search instead
 * Fetches all purchase orders for a facility (V1 API).
 */
export const useAllPurchaseOrdersQuery = ({ enabled = true, facilityId, id = PURCHASE_ORDERS_QUERY_KEY }) =>
    useQuery({
        queryKey: ['purchase-orders', id, facilityId ?? 'all'],
        enabled,
        placeholderData: (previousData) => previousData ?? { rows: [] },
        queryFn: async () => {
            const response = await procurementApi.getPurchaseOrders(facilityId ?? 0);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch purchase orders');
            }
            return response?.data ?? { rows: [] };
        },
        select: (data) => ({
            rows: Array.isArray(data?.rows) ? data.rows : [],
            summary: data,
        }),
    });

/**
 * Fetches the details of a purchase order.
 *
 * @param {Object} [options]
 * @param {number|string} [options.id] - Purchase order identifier required by the API.
 * @param {boolean} [options.enabled=true] - Enables the query when true.
 * @returns {import('@tanstack/react-query').UseQueryResult<Object, Error>}
 */
export const usePurchaseOrderQuery = ({ enabled = true, id, options = {} }) => {
    const isEnabled = enabled && Boolean(id) && (options.enabled ?? true);

    return useQuery({
        queryKey: purchaseOrderQueryKeys.detail(id),
        queryFn: async () => {
            if (!id) return null;
            const response = await procurementApi.getPurchaseOrderDetails(id);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch purchase order');
            }
            return response?.data?.output ?? response?.data ?? null;
        },
        placeholderData: null,
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        ...options,
        enabled: isEnabled,
    });
};
